import {
  assignPrototypeAPIs,
  assignTableAPIs,
  type CellData,
  type Column_Internal,
  type RowData,
  type Table,
  type Table_Internal,
  type TableFeature,
  type TableFeatures,
  type TableOptions_All,
} from '@tanstack/react-table';
import {
  column_getIsPinned,
  column_getIsVisible,
  column_getLeafColumns,
  table_getCenterVisibleLeafColumns,
  table_getLeftVisibleLeafColumns,
  table_getRightVisibleLeafColumns,
  table_getVisibleLeafColumns,
} from '@tanstack/react-table/static-functions';

import type { ColumnVisibilityFeatureConstructors } from './column-visibility-types.js';

/**
 * Helper function to not duplicate this logic for the slightly different use cases of user actions and column settings modal.
 * @internal
 */
export function getCanHide<
  TFeatures extends TableFeatures,
  TData extends RowData,
  TValue extends CellData = CellData,
>(
  table: Table<TFeatures, TData>,
  column: Column_Internal<TFeatures, TData, TValue>,
  isHideable: (
    columns: Array<Column_Internal<TFeatures, TData, TValue>>,
  ) => boolean,
): boolean {
  // The feature is disabled altogether.
  if (
    (table.options as TableOptions_All<TFeatures, TData>).enableHiding === false
  ) {
    return false;
  }

  // It is a leaf column, just look at the `enableHiding` for this column
  if (!column.columns || column.columns.length === 0) {
    return column.columnDef.enableHiding ?? true;
  }

  // It has sub-columns.
  // `enableHiding` will be false if it's explicitly set to false
  // Otherwise check the provided function
  return column.columnDef.enableHiding === false
    ? false
    : isHideable(column.columns);
}

/**
 * @returns true if *all* child columns can be hidden, false otherwise.
 */
function isParentColumnHideable<
  TFeatures extends TableFeatures,
  TData extends RowData,
  TValue extends CellData = CellData,
>(childColumns: Array<Column_Internal<TFeatures, TData, TValue>>): boolean {
  return childColumns.every((c) => c.columnDef.enableHiding);
}

export function table_getOrderedVisibleLeafColumns<
  TFeatures extends TableFeatures,
  TData extends RowData,
>(table: Table_Internal<TFeatures, TData>) {
  return [
    ...table_getLeftVisibleLeafColumns(table),
    ...table_getCenterVisibleLeafColumns(table),
    ...table_getRightVisibleLeafColumns(table),
  ];
}

/**
 * Helper function that provides an array of all visible leaf-node columns, excluding builtin columns.
 * @internal
 */
export function table_getVisibleLeafColumnsWithoutBuiltin<
  TFeatures extends TableFeatures,
  TData extends RowData,
>(table: Table_Internal<TFeatures, TData>) {
  return table_getVisibleLeafColumns(table).filter(
    (leafColumn) => !leafColumn.columnDef.meta?.builtin,
  );
}

/**
 * Helper function that provides an array of all visible leaf-node columns, excluding builtin columns.
 * @internal
 */
export function table_getAllLeafColumnsWithoutBuiltin<
  TFeatures extends TableFeatures,
  TData extends RowData,
>(
  table: Table_Internal<TFeatures, TData>,
  allLeafColumns: Array<Column_Internal<TFeatures, TData>>,
): Array<Column_Internal<TFeatures, TData>> {
  return allLeafColumns.filter((column) => !column.columnDef.meta?.builtin);
}

/**
 * Helper function that provides an array of all center visible leaf-node columns, excluding builtin columns.
 * @internal
 */
export function table_getCenterVisibleLeafColumnsWithoutBuiltIn<
  TFeatures extends TableFeatures,
  TData extends RowData,
>(table: Table_Internal<TFeatures, TData>) {
  return table_getCenterVisibleLeafColumns(table).filter(
    (leafColumn) => !leafColumn.columnDef.meta?.builtin,
  );
}

/**
 * Helper function that provides the ids of all visible leaf-node columns that are currently rendered by the virtualizer, excluding builtin columns.
 * @internal
 */
export function table_getVisibleVirtualColumnIdsWithoutBuiltin<
  TFeatures extends TableFeatures,
  TData extends RowData,
>(table: Table_Internal<TFeatures, TData>) {
  const visibleLeafColumns = table_getVisibleLeafColumnsWithoutBuiltin(table);
  const virtualColumns = table.getColumnVirtualizer()?.getVirtualItems() ?? [];

  return virtualColumns.flatMap(
    (virtualItem) => visibleLeafColumns.at(virtualItem.index)?.id ?? [],
  );
}

/**
 * Helper function that provides an array of all visible leaf-node columns for a given column, excluding builtin columns.
 * @internal
 */
export function column_getIsLastVisibleColumn<
  TFeatures extends TableFeatures,
  TData extends RowData,
  TValue extends CellData = CellData,
>(column: Column_Internal<TFeatures, TData, TValue>) {
  // Find the last visible column, prioritizing right-pinned columns, then center columns
  const lastVisibleLeafColumnId =
    table_getRightVisibleLeafColumns(column.table).at(-1)?.id ??
    table_getCenterVisibleLeafColumns(column.table).at(-1)?.id;

  // return true if the current column or one of its child columns is the last visible column
  return (
    column.id === lastVisibleLeafColumnId ||
    column.columns.some(
      (childColumn) => childColumn.id === lastVisibleLeafColumnId,
    )
  );
}

/**
 * Helper function that provides an array of all visible leaf-node columns for a given column.
 * @internal
 */
export function column_getVisibleLeafColumns<
  TFeatures extends TableFeatures,
  TData extends RowData,
  TValue extends CellData = CellData,
>(column: Column_Internal<TFeatures, TData, TValue>) {
  return column_getLeafColumns(column).filter((leafColumn) =>
    column_getIsVisible(leafColumn),
  );
}

/**
 * Helper function that provides an array of all visible center leaf-node columns for a given column.
 * @internal
 */
export function column_getCenterVisibleLeafColumns<
  TFeatures extends TableFeatures,
  TData extends RowData,
  TValue extends CellData = CellData,
>(column: Column_Internal<TFeatures, TData, TValue>) {
  return column_getVisibleLeafColumns(column).filter(
    (leafColumn) => !column_getIsPinned(leafColumn),
  );
}

/**
 * Helper function that provides an array of all visible left leaf-node columns for a given column.
 * @internal
 */
export function column_getLeftVisibleLeafColumns<
  TFeatures extends TableFeatures,
  TData extends RowData,
  TValue extends CellData = CellData,
>(column: Column_Internal<TFeatures, TData, TValue>) {
  const ids = new Set(column_getVisibleLeafColumns(column).map((c) => c.id));
  return table_getLeftVisibleLeafColumns(column.table).filter((c) =>
    ids.has(c.id),
  );
}
/**
 * Helper function that provides an array of all visible right leaf-node columns for a given column.
 * @internal
 */
export function column_getRightVisibleLeafColumns<
  TFeatures extends TableFeatures,
  TData extends RowData,
  TValue extends CellData = CellData,
>(column: Column_Internal<TFeatures, TData, TValue>) {
  const ids = new Set(column_getVisibleLeafColumns(column).map((c) => c.id));
  return table_getRightVisibleLeafColumns(column.table).filter((c) =>
    ids.has(c.id),
  );
}

/**
 * Helper function that provides whether a column can be hidden or not, based on the `enableHiding` property of the column definition and its parent columns.
 * @internal
 */
export function column_getCanHide<
  TFeatures extends TableFeatures,
  TData extends RowData,
  TValue extends CellData = CellData,
>(column: Column_Internal<TFeatures, TData, TValue>) {
  return getCanHide(column.table, column, isParentColumnHideable);
}

/**
 * Constructs the column visibility feature for the table,
 * including API implementations and prototype assignments.
 * @internal
 */
export function constructColumnVisiblityFeature<
  TFeatures extends TableFeatures,
  TData extends RowData,
>(): TableFeature<ColumnVisibilityFeatureConstructors<TFeatures, TData>> {
  return {
    assignColumnPrototype: (prototype, table) => {
      assignPrototypeAPIs('columnVisibilityFeature', prototype, table, {
        column_getIsLastVisibleColumn: {
          fn: (column) => column_getIsLastVisibleColumn(column),
          memoDeps: () => [
            table.atoms.columnVisibility?.get(),
            table.atoms.columnPinning?.get(),
            table.options.columns,
          ],
        },
        column_getVisibleLeafColumns: {
          fn: (column) => column_getVisibleLeafColumns(column),
          memoDeps: () => [
            table.atoms.columnVisibility?.get(),
            table.atoms.columnOrder?.get(),
            table.options.columns,
          ],
        },
        column_getCenterVisibleLeafColumns: {
          fn: (column) => column_getCenterVisibleLeafColumns(column),
          memoDeps: () => [
            table.atoms.columnVisibility?.get(),
            table.atoms.columnPinning?.get(),
            table.atoms.columnOrder?.get(),
            table.options.columns,
          ],
        },
        column_getLeftVisibleLeafColumns: {
          fn: (column) => column_getLeftVisibleLeafColumns(column),
          memoDeps: () => [
            table.atoms.columnVisibility?.get(),
            table.atoms.columnPinning?.get()?.left,
            table.atoms.columnOrder?.get(),
            table.options.columns,
          ],
        },
        column_getRightVisibleLeafColumns: {
          fn: (column) => column_getRightVisibleLeafColumns(column),
          memoDeps: () => [
            table.atoms.columnVisibility?.get(),
            table.atoms.columnPinning?.get()?.right,
            table.atoms.columnOrder?.get(),
            table.options.columns,
          ],
        },
        column_getCanHide: {
          fn: (column) => column_getCanHide(column),
        },
      });
    },
    constructTableAPIs: (table) => {
      assignTableAPIs('columnVisibilityFeature', table, {
        table_getAllLeafColumnsWithoutBuiltin: {
          fn: (leafColumns) =>
            table_getAllLeafColumnsWithoutBuiltin(table, leafColumns),
          memoDeps: () => [table.getAllLeafColumns()],
        },
        table_getOrderedVisibleLeafColumns: {
          fn: () => table_getOrderedVisibleLeafColumns(table),
          memoDeps: () => [
            table.atoms.columnVisibility?.get(),
            table.atoms.columnPinning?.get(),
            table.atoms.columnOrder?.get(),
            table.options.columns,
          ],
        },
        table_getVisibleLeafColumnsWithoutBuiltin: {
          fn: () => table_getVisibleLeafColumnsWithoutBuiltin(table),
          memoDeps: () => [
            table.atoms.columnVisibility?.get(),
            table.atoms.columnOrder?.get(),
            table.options.columns,
          ],
        },
        table_getCenterVisibleLeafColumnsWithoutBuiltIn: {
          fn: () => table_getCenterVisibleLeafColumnsWithoutBuiltIn(table),
          memoDeps: () => [
            table.atoms.columnVisibility?.get(),
            table.atoms.columnPinning?.get(),
            table.atoms.columnOrder?.get(),
            table.options.columns,
          ],
        },
        table_getVisibleVirtualColumnIdsWithoutBuiltin: {
          fn: () => table_getVisibleVirtualColumnIdsWithoutBuiltin(table),
        },
      });
    },
  };
}

/**
 * Additional feature implementation for column Visiblity.
 * @internal
 */
export const DataTableColumnVisiblityFeature =
  constructColumnVisiblityFeature();
